import React from 'react';
import {
  DialogMessageNotification,
  DialogMessageRenderer,
  DialogStateShape,
  DialogTextMessage,
  MessageWithActionsLayout,
  coreDialogApi,
  generateDraftId,
  useCurrentDialogMember,
  useDialogTheme,
} from '@cp/entities/dialog';
import { MessageType } from '@common/model/dialog/message/messageType';
import { Typography } from '@cp/ds/src/components/typography';
import { Stack } from '@cp/ds/src/components/stack';
import { Button } from '@cp/ds/src/components/button';
import { DialogMessageFavoriteResponsePayload } from '@common/model/dialog/message/payload/favoriteResponse';
import { DialogMessageCreateRequest } from '@common/model/dialog/message/dialogMessageCreateRequest';
import { dialogMembersSelector } from '@cp/entities/dialog/model/store/selectors';
import { DialogMemberType } from '@common/model/dialog/dialogMemberType';
import { useSelector } from 'react-redux';

export const FavoriteNotificationRenderer: DialogMessageRenderer<MessageType.NotificationFavorite> = (props) => {
  const { message, dialogId } = props;
  const currentMember = useCurrentDialogMember({ dialogId });
  const isCurrentUserSubject = currentMember?.entityId === message.payload.subjectClientId;
  const members = useSelector<DialogStateShape, ReturnType<typeof dialogMembersSelector>>((state) =>
    dialogMembersSelector(state, dialogId),
  );
  const favoriteMember = members.find((m) => m.entityId === message.payload.subjectClientId);
  const [sendMessage, { isLoading }] = coreDialogApi.useSendMessageMutation();
  const [isResponded, setResponded] = React.useState(Boolean(message.replies?.length));
  const { listGap } = useDialogTheme();

  if (message.author.type === DialogMemberType.Bot) {
    return <DialogMessageNotification {...props} text="Вы добавлены в список избранных" />;
  }

  const messageText = `Здравствуйте${favoriteMember?.name ? ` ${favoriteMember?.name}` : ''}, вы работаете няней?`;

  if (props.isPreview) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{messageText}</>;
  }

  const createResponseToFavoriteHandler = (isPositive: boolean) => async () => {
    const payload: DialogMessageFavoriteResponsePayload = {
      isPositive,
      responseToClientId: message.payload.clientId,
    };
    await sendMessage({
      type: MessageType.NotificationFavoriteResponse,
      payload: payload as unknown as DialogMessageCreateRequest['payload'],
      dialogId: dialogId as number,
      draftId: generateDraftId(dialogId as number, '', MessageType.NotificationFavoriteResponse),
      parentId: message.id,
    });
    setResponded(true);
  };

  return (
    <Stack gap={listGap}>
      {messageText && <DialogTextMessage {...props} message={{ ...message, payload: messageText }} />}

      {!isResponded && isCurrentUserSubject && (
        <MessageWithActionsLayout>
          <Typography variant="body bold">Нанимателю понравилась ваша анкета, он ждет ответ</Typography>
          <Stack alignItems="center" direction="row" spacing={1} sx={{ mt: 1, width: 1 }}>
            <Button
              color="gray"
              disabled={isLoading}
              onClick={createResponseToFavoriteHandler(true)}
              size="extraSmall"
              sx={{ flex: '1 1 0px' }}
              variant="primary"
            >
              Да, работаю
            </Button>
            <Button
              color="pink"
              disabled={isLoading}
              onClick={createResponseToFavoriteHandler(false)}
              size="extraSmall"
              sx={{ flex: '1 1 0px' }}
              variant="primary"
            >
              Нет
            </Button>
          </Stack>
        </MessageWithActionsLayout>
      )}
    </Stack>
  );
};
